import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import MuiLink from '@mui/material/Link';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import apiClient from '../services/apiClient';
import { keyframes } from '@mui/system';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Register = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: role selection, 2: form
  const [selectedRole, setSelectedRole] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    // Physician-specific fields
    medicalLicense: '',
    specialty: '',
    yearsExperience: '',
    // Sponsor-specific fields
    organizationName: ''
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const validateForm = () => {
    let valid = true;
    const newErrors = {};

    // First Name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
      valid = false;
    } else if (formData.firstName.length < 2) {
      newErrors.firstName = 'First name must be at least 2 characters';
      valid = false;
    }

    // Last Name validation
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
      valid = false;
    } else if (formData.lastName.length < 2) {
      newErrors.lastName = 'Last name must be at least 2 characters';
      valid = false;
    }

    // Role-specific validation
    if (selectedRole === 'physician') {
      if (!formData.medicalLicense.trim()) {
        newErrors.medicalLicense = 'Medical license number is required';
        valid = false;
      }
      if (!formData.specialty.trim()) {
        newErrors.specialty = 'Specialty is required';
        valid = false;
      }
    }

    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
      valid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
      valid = false;
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
      valid = false;
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
      valid = false;
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
      valid = false;
    }

    // Confirm Password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
      valid = false;
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsLoading(true);
      setErrors({});
      setSuccessMessage('');
      
      try {
        // Prepare registration data for backend API
        const registrationData = {
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          role: selectedRole
        };
        
        // Add role-specific fields
        if (selectedRole === 'physician') {
          registrationData.medicalLicenseNumber = formData.medicalLicense;
          registrationData.specialty = formData.specialty;
          if (formData.yearsExperience) {
            registrationData.yearsExperience = parseInt(formData.yearsExperience);
          }
        } else if (selectedRole === 'sponsor') {
          if (formData.organizationName) {
            registrationData.organizationName = formData.organizationName;
          }
        }
        
        // Call backend API for registration
        const response = await apiClient.register(registrationData);
        
        if (response && response.data) {
          // Show role-specific success message
          const roleMessage = selectedRole === 'physician' 
            ? 'Your application to join as a volunteer physician has been submitted! Please check your email to confirm your account. Your account will be reviewed by our team within 2-3 business days.'
            : selectedRole === 'sponsor'
            ? 'Your application to join as a sponsor has been submitted! Please check your email to confirm your account. Your account will be reviewed by our team within 2-3 business days.'
            : 'Account created successfully! Please check your email to confirm your account before signing in.';
          
          setSuccessMessage(roleMessage);
          
          // Clear form
          setFormData({
            firstName: '',
            lastName: '',
            email: '',
            password: '',
            confirmPassword: '',
            medicalLicense: '',
            specialty: '',
            yearsExperience: '',
            organizationName: ''
          });
          
          // Redirect to login after 5 seconds
          setTimeout(() => {
            navigate('/login');
          }, 5000);
        }
      } catch (error) {
        console.error('Error registering user:', error);
        let errorMessage = 'An error occurred during registration';
        
        // Handle API error responses
        if (error.message) {
          if (error.message.includes('already registered') || error.message.includes('Email already')) {
            errorMessage = 'An account with this email already exists';
          } else if (error.message.includes('Password')) {
            errorMessage = 'Password is too weak. Please use a stronger password';
          } else if (error.message.includes('Missing required fields')) {
            errorMessage = 'Please fill in all required fields';
          } else if (error.message.includes('Failed to fetch') || error.message.includes('Network')) {
            errorMessage = 'Unable to connect to server. Please check your internet connection and try again.';
          } else {
            errorMessage = error.message;
          }
        }
        
        setErrors({ ...errors, submit: errorMessage });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        bgcolor: 'background.default',
        py: 4,
      }}
    >
      <Container maxWidth="xs" sx={{ px: { xs: 1, sm: 2 }, width: '100%', maxWidth: '100%' }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ fontWeight: 400, fontSize: { xs: '1rem', sm: '1.1rem' } }}
          >
            Create your account to access our telemedicine services
          </Typography>
        </Box>
        
        <Box sx={{ maxWidth: 500, mx: 'auto' }}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: { xs: 2, sm: 3 }, 
                width: '100%', 
                maxWidth: 400, 
                mx: 'auto', 
                overflowX: 'auto',
                borderRadius: 2,
                animation: `${fadeIn} 0.5s ease-out`,
                '&:hover': {
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
                },
                transition: 'box-shadow 0.3s ease-in-out',
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {step === 1 ? (
                  <>
                    <Typography 
                      component={Link}
                      to="/"
                      variant="h4" 
                      sx={{ 
                        mb: 2, 
                        color: 'info.main', 
                        fontWeight: 600,
                        letterSpacing: '-0.5px',
                        fontSize: { xs: '1.25rem', sm: '1.5rem' },
                        textDecoration: 'none',
                        cursor: 'pointer',
                        '&:hover': {
                          textDecoration: 'underline',
                        }
                      }}
                    >
                      Join Alpha01
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                      Select how you'd like to contribute to our mission
                    </Typography>
                    
                    <ToggleButtonGroup
                      value={selectedRole}
                      exclusive
                      onChange={(e, value) => value && setSelectedRole(value)}
                      orientation="vertical"
                      fullWidth
                      sx={{ mb: 3 }}
                    >
                      <ToggleButton 
                        value="physician" 
                        sx={{ 
                          py: 2.5,
                          textAlign: 'left',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          border: '2px solid',
                          borderColor: selectedRole === 'physician' ? 'primary.main' : 'divider',
                          '&.Mui-selected': {
                            bgcolor: 'primary.light',
                            color: 'primary.main',
                            '&:hover': {
                              bgcolor: 'primary.light',
                            }
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 0.5 }}>
                          <LocalHospitalIcon sx={{ mr: 1 }} />
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Physician (Volunteer)
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ pl: 4 }}>
                          Provide virtual consultations to patients in need
                        </Typography>
                      </ToggleButton>
                      
                      <ToggleButton 
                        value="sponsor" 
                        sx={{ 
                          py: 2.5,
                          textAlign: 'left',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          border: '2px solid',
                          borderColor: selectedRole === 'sponsor' ? 'secondary.main' : 'divider',
                          '&.Mui-selected': {
                            bgcolor: 'secondary.light',
                            color: 'secondary.main',
                            '&:hover': {
                              bgcolor: 'secondary.light',
                            }
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 0.5 }}>
                          <VolunteerActivismIcon sx={{ mr: 1 }} />
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Sponsor (Donor)
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ pl: 4 }}>
                          Fund healthcare for patients who need support
                        </Typography>
                      </ToggleButton>
                    </ToggleButtonGroup>
                    
                    <Button
                      fullWidth
                      variant="contained"
                      disabled={!selectedRole}
                      onClick={() => setStep(2)}
                      sx={{ mb: 2, py: 1.5 }}
                    >
                      Continue
                    </Button>
                    
                    <Alert severity="info" sx={{ mt: 2, textAlign: 'left' }}>
                      <Typography variant="body2">
                        <strong>Looking for patient care?</strong><br />
                        Please contact your local clinic to create your patient account.
                      </Typography>
                    </Alert>
                  </>
                ) : (
                  <>
                    <Button
                      size="small"
                      onClick={() => setStep(1)}
                      sx={{ alignSelf: 'flex-start', mb: 2 }}
                    >
                      ← Change Role
                    </Button>
                    
                    <Typography 
                      component="h2" 
                      variant="h4" 
                      sx={{ 
                        mb: 1, 
                        color: 'info.main', 
                        fontWeight: 600,
                        letterSpacing: '-0.5px',
                        fontSize: { xs: '1.25rem', sm: '1.5rem' },
                      }}
                    >
                      {selectedRole === 'physician' ? 'Physician Application' : 'Sponsor Application'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                      Your application will be reviewed within 2-3 business days
                    </Typography>
                    <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
                      <Typography variant="body2">
                        <strong>Platform Volunteer:</strong> You're joining Alpha01 as a platform volunteer. After approval, you'll be able to work with one or more clinics.
                      </Typography>
                    </Alert>
                  </>
                )}
                
                {successMessage && (
                  <Alert 
                    severity="success" 
                    sx={{ 
                      width: '100%', 
                      mb: 2,
                      animation: `${fadeIn} 0.3s ease-out`,
                    }}
                  >
                    {successMessage}
                  </Alert>
                )}
                
                {errors.submit && (
                  <Alert 
                    severity="error" 
                    sx={{ 
                      width: '100%', 
                      mb: 2,
                      animation: `${fadeIn} 0.3s ease-out`,
                    }}
                  >
                    {errors.submit}
                  </Alert>
                )}
                
                {step === 2 && (
                <Box component="form" sx={{ width: '100%' }} onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="firstName"
                        label="First Name"
                        fullWidth
                        required
                        value={formData.firstName}
                        onChange={handleChange}
                        error={!!errors.firstName}
                        helperText={errors.firstName}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                              borderColor: 'primary.main',
                            },
                          },
                        }}
                        inputProps={{ style: { fontSize: '1rem', padding: '12px 14px' } }}
                        InputLabelProps={{ style: { fontSize: '1rem' } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="lastName"
                        label="Last Name"
                        fullWidth
                        required
                        value={formData.lastName}
                        onChange={handleChange}
                        error={!!errors.lastName}
                        helperText={errors.lastName}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                              borderColor: 'primary.main',
                            },
                          },
                        }}
                        inputProps={{ style: { fontSize: '1rem', padding: '12px 14px' } }}
                        InputLabelProps={{ style: { fontSize: '1rem' } }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        name="email"
                        label="Email Address"
                        fullWidth
                        required
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        error={!!errors.email}
                        helperText={errors.email}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                              borderColor: 'primary.main',
                            },
                          },
                        }}
                        inputProps={{ style: { fontSize: '1rem', padding: '12px 14px' } }}
                        InputLabelProps={{ style: { fontSize: '1rem' } }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        name="password"
                        label="Password"
                        fullWidth
                        required
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={handleChange}
                        error={!!errors.password}
                        helperText={errors.password}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={handleTogglePasswordVisibility}
                                edge="end"
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                              borderColor: 'primary.main',
                            },
                          },
                        }}
                        inputProps={{ 
                          style: { fontSize: '1rem', padding: '12px 14px' },
                          autoComplete: 'new-password'
                        }}
                        InputLabelProps={{ style: { fontSize: '1rem' } }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        name="confirmPassword"
                        label="Confirm Password"
                        fullWidth
                        required
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        error={!!errors.confirmPassword}
                        helperText={errors.confirmPassword}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle confirm password visibility"
                                onClick={handleToggleConfirmPasswordVisibility}
                                edge="end"
                              >
                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                              borderColor: 'primary.main',
                            },
                          },
                        }}
                        inputProps={{ 
                          style: { fontSize: '1rem', padding: '12px 14px' },
                          autoComplete: 'new-password'
                        }}
                        InputLabelProps={{ style: { fontSize: '1rem' } }}
                      />
                    </Grid>
                    
                    {/* Role-specific fields */}
                    {selectedRole === 'physician' && (
                      <>
                        <Grid item xs={12}>
                          <Divider sx={{ my: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              Medical Credentials
                            </Typography>
                          </Divider>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            name="medicalLicense"
                            label="Medical License Number"
                            fullWidth
                            required
                            value={formData.medicalLicense}
                            onChange={handleChange}
                            error={!!errors.medicalLicense}
                            helperText={errors.medicalLicense}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '&:hover fieldset': {
                                  borderColor: 'primary.main',
                                },
                              },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={8}>
                          <TextField
                            name="specialty"
                            label="Medical Specialty"
                            fullWidth
                            required
                            value={formData.specialty}
                            onChange={handleChange}
                            error={!!errors.specialty}
                            helperText={errors.specialty}
                            placeholder="e.g., Family Medicine, Cardiology"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '&:hover fieldset': {
                                  borderColor: 'primary.main',
                                },
                              },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            name="yearsExperience"
                            label="Years of Experience"
                            type="number"
                            fullWidth
                            value={formData.yearsExperience}
                            onChange={handleChange}
                            inputProps={{ min: 0, max: 70 }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '&:hover fieldset': {
                                  borderColor: 'primary.main',
                                },
                              },
                            }}
                          />
                        </Grid>
                      </>
                    )}
                    
                    {selectedRole === 'sponsor' && (
                      <>
                        <Grid item xs={12}>
                          <Divider sx={{ my: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              Organization (Optional)
                            </Typography>
                          </Divider>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            name="organizationName"
                            label="Organization Name"
                            fullWidth
                            value={formData.organizationName}
                            onChange={handleChange}
                            placeholder="Leave blank for individual sponsorship"
                            helperText="If sponsoring on behalf of an organization"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '&:hover fieldset': {
                                  borderColor: 'primary.main',
                                },
                              },
                            }}
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>
                  
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    color="secondary"
                    disabled={isLoading}
                    sx={{ 
                      mt: 3, 
                      mb: 2, 
                      py: 1.5,
                      position: 'relative',
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      '&:hover': {
                        transform: 'translateY(-1px)',
                      },
                      transition: 'transform 0.2s ease-in-out',
                    }}
                  >
                    {isLoading ? 'Creating Account...' : 'Sign Up'}
                  </Button>
                  
                  <Divider sx={{ my: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      OR
                    </Typography>
                  </Divider>
                  
                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <MuiLink 
                      component={Link} 
                      to="/login" 
                      variant="body1" 
                      color="info.main"
                      sx={{
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                      }}
                    >
                      Already have an account? Sign In
                    </MuiLink>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 3, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    By signing up, you agree to our{' '}
                    <MuiLink 
                      component={Link} 
                      to="/terms" 
                      color="info.main"
                      sx={{
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      Terms of Service
                    </MuiLink>{' '}
                    and{' '}
                    <MuiLink 
                      component={Link} 
                      to="/privacy" 
                      color="info.main"
                      sx={{
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      Privacy Policy
                    </MuiLink>
                  </Typography>
                </Box>
                )}
              </Box>
            </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default Register;
